import React from "react";
import { Box } from "@mui/material";

// Phieg "O" ring mark — same shape as the brand/explainer-video logo:
// a dark disc, a thick green ring, and a dark center.
const RING = "#A9D98B"; // Phieg logo green
const DISC = "#20211E"; // logo dark disc/center

// Types "Phieg" letter-by-letter (the load-complete flourish under the ring).
function PhiegWordmark({ typeMs = 95 }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= 5) clearInterval(id);
    }, typeMs);
    return () => clearInterval(id);
  }, [typeMs]);
  return (
    <Box
      sx={{
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 26,
        fontWeight: 800,
        letterSpacing: "0.08em",
        color: "#eaf2ef",
        minHeight: 32,
        lineHeight: "32px",
      }}
    >
      {"Phieg".slice(0, n)}
    </Box>
  );
}

/**
 * The Phieg "O" as the app-wide loader.
 *  - determinate: pass `value` 0–100 — the green ring DRAWS ON to match how far
 *    along the task is (the logo "completes" as the task completes).
 *  - indeterminate: omit `value` — a green arc spins.
 *  - overlay / fullscreen: show it on a slightly-transparent dark veil (a real
 *    loading screen), as in the video's intro/outro.
 */
export default function PhiegLoader({
  value,
  size = 72,
  overlay = false,
  fullscreen = false,
  label,
  wordmark = false,
  // compact: mark only — no % readout or label row. For small inline uses
  // (e.g. the 28px pull-to-refresh indicator) where text would not fit.
  compact = false,
  fillMs = 350,
  typeMs = 95,
}) {
  const determinate = typeof value === "number" && !Number.isNaN(value);
  const r = 30;
  const circ = 2 * Math.PI * r;
  const pct = determinate ? Math.max(0, Math.min(100, value)) : 30;
  const offset = circ * (1 - pct / 100);
  const onVeil = overlay || fullscreen;

  const mark = (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
      }}
    >
      <Box
        sx={{
          width: size,
          height: size,
          lineHeight: 0,
          "@keyframes phiegSpin": { to: { transform: "rotate(360deg)" } },
          animation: determinate ? "none" : "phiegSpin 1.05s linear infinite",
        }}
      >
        <svg width={size} height={size} viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="48" fill={DISC} />
          {determinate && (
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="rgba(169, 217, 139, 0.18)"
              strokeWidth="16"
            />
          )}
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={RING}
            strokeWidth="16"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            transform="rotate(-90 50 50)"
            style={{
              transition: determinate
                ? `stroke-dashoffset ${fillMs}ms ease`
                : "none",
            }}
          />
          <circle cx="50" cy="50" r="15" fill={DISC} />
        </svg>
      </Box>
      {compact ? null : wordmark ? (
        <PhiegWordmark typeMs={typeMs} />
      ) : (
        <>
          {determinate && (
            <Box sx={{ fontSize: 12, fontWeight: 700, color: RING }}>
              {Math.round(pct)}%
            </Box>
          )}
          {label && (
            <Box sx={{ fontSize: 13, color: "#eaf2ef" }}>{label}</Box>
          )}
        </>
      )}
    </Box>
  );

  if (!onVeil) return mark;

  return (
    <Box
      sx={{
        position: fullscreen ? "fixed" : "absolute",
        inset: 0,
        zIndex: fullscreen ? 1400 : 5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(26, 42, 39, 0.55)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
      }}
    >
      {mark}
    </Box>
  );
}
